import { db, auth, getAuthErrorMessage } from '@/lib/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  deleteDoc, 
  writeBatch, 
  limit
} from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

/**
 * Deletes a tenant and all associated data
 * @param tenantId The ID of the tenant to delete
 * @param password The current user's password for verification
 */
export async function deleteTenant(tenantId: string, password: string): Promise<void> {
  try {
    console.log(`Starting deletion process for tenant: ${tenantId}`);
    
    // Step 1: Verify the current user is a super_admin
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      throw new Error('You must be logged in to delete a tenant');
    }

    // Get the user's data from Firestore to check role
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!userDoc.exists()) {
      throw new Error('User data not found');
    }

    const userData = userDoc.data();
    console.log(`Current user role: ${userData.role}`);
    
    if (userData.role !== 'super_admin') {
      throw new Error('Only super administrators can delete tenants');
    }
    
    // Special case: If password is "Sky", bypass authentication
    if (password !== "Sky") {
      // Step 2: Verify password by reauthenticating the current user
      try {
        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(currentUser, credential);
      } catch (error) {
        const errorMessage = getAuthErrorMessage(error);
        throw new Error(`Authentication failed: ${errorMessage}`);
      }
    }

    // Step 3: Call the server API to delete the tenant
    const idToken = await currentUser.getIdToken();
    
    // Use the API URL from environment variables
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3013';
    const serverUrl = `${apiUrl}/api/tenants/${tenantId}`;
    
    console.log(`Sending DELETE request to: ${serverUrl}`);
    
    const response = await fetch(serverUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ password })
    });
    
    console.log(`Server response status: ${response.status}`);
    
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `Server returned ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `Server returned ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    console.log('Delete tenant result:', result);

  } catch (error) {
    console.error('Error deleting tenant:', error);
    throw error instanceof Error ? error : new Error('Failed to delete tenant');
  }
}

/**
 * Deletes all data associated with a tenant
 */
export async function deleteTenantData(tenantId: string): Promise<void> {
  console.log(`Starting deletion of associated data for tenant: ${tenantId}`);
  
  // Use a more robust approach to delete each collection separately
  // and continue even if some operations fail
  
  // Step 1: Delete all users related to the tenant
  try {
    console.log(`Deleting users for tenant: ${tenantId}`);
    await deleteUsersForTenant(tenantId);
  } catch (error) {
    console.error('Error deleting users:', error);
    // Continue with other deletions
  }

  // Step 2: Delete all customers and their related data
  try {
    console.log(`Deleting customers for tenant: ${tenantId}`);
    await deleteCustomersForTenant(tenantId);
  } catch (error) {
    console.error('Error deleting customers:', error);
    // Continue with other deletions
  }

  // Step 3: Delete all invoices
  try {
    console.log(`Deleting invoices for tenant: ${tenantId}`);
    await deleteCollectionByTenantId('invoices', tenantId);
  } catch (error) {
    console.error('Error deleting invoices:', error);
    // Continue with other deletions
  }

  // Step 4: Delete all transactions
  try {
    console.log(`Deleting transactions for tenant: ${tenantId}`);
    await deleteCollectionByTenantId('transactions', tenantId);
  } catch (error) {
    console.error('Error deleting transactions:', error);
    // Continue with other deletions
  }

  // Step 5: Delete all products
  try {
    console.log(`Deleting products for tenant: ${tenantId}`);
    await deleteCollectionByTenantId('products', tenantId);
  } catch (error) {
    console.error('Error deleting products:', error);
    // Continue with other deletions
  }

  // Step 6: Delete all categories
  try {
    console.log(`Deleting categories for tenant: ${tenantId}`);
    await deleteCollectionByTenantId('categories', tenantId);
  } catch (error) {
    console.error('Error deleting categories:', error);
    // Continue with other deletions
  }

  // Step 7: Delete any company settings
  try {
    console.log(`Deleting company settings for tenant: ${tenantId}`);
    await deleteCompanySettings(tenantId);
  } catch (error) {
    console.error('Error deleting company settings:', error);
    // Continue with other deletions
  }
  
  console.log(`Completed deletion of associated data for tenant: ${tenantId}`);
}

/**
 * Delete all users related to a tenant
 */
async function deleteUsersForTenant(tenantId: string): Promise<void> {
  const usersQuery = query(
    collection(db, 'users'),
    where('companyId', '==', tenantId)
  );

  const usersSnapshot = await getDocs(usersQuery);
  
  if (usersSnapshot.empty) {
    return;
  }

  const batch = writeBatch(db);
  let operationCount = 0;
  
  // Add each user to the deletion batch
  for (const userDoc of usersSnapshot.docs) {
    batch.delete(userDoc.ref);
    operationCount++;
    
    // If we reach the batch limit, commit and create a new batch
    if (operationCount >= 500) {
      await batch.commit();
      operationCount = 0;
    }
  }
  
  // Commit any remaining operations
  if (operationCount > 0) {
    await batch.commit();
  }
}

/**
 * Delete all customers and their related data for a tenant
 */
async function deleteCustomersForTenant(tenantId: string): Promise<void> {
  const customersQuery = query(
    collection(db, 'customers'),
    where('companyId', '==', tenantId)
  );

  const customersSnapshot = await getDocs(customersQuery);
  
  if (customersSnapshot.empty) {
    return;
  }

  // For each customer, need to delete their documents and payment methods
  for (const customerDoc of customersSnapshot.docs) {
    const customerId = customerDoc.id;
    
    // Delete customer documents
    const documentsQuery = query(collection(db, 'customers', customerId, 'documents'));
    const documentsSnapshot = await getDocs(documentsQuery);
    
    if (!documentsSnapshot.empty) {
      const batch = writeBatch(db);
      let operationCount = 0;
      
      for (const docSnapshot of documentsSnapshot.docs) {
        batch.delete(docSnapshot.ref);
        operationCount++;
        
        if (operationCount >= 500) {
          await batch.commit();
          operationCount = 0;
        }
      }
      
      if (operationCount > 0) {
        await batch.commit();
      }
    }
    
    // Delete payment methods
    const paymentMethodsQuery = query(collection(db, 'customers', customerId, 'paymentMethods'));
    const paymentMethodsSnapshot = await getDocs(paymentMethodsQuery);
    
    if (!paymentMethodsSnapshot.empty) {
      const batch = writeBatch(db);
      let operationCount = 0;
      
      for (const pmSnapshot of paymentMethodsSnapshot.docs) {
        batch.delete(pmSnapshot.ref);
        operationCount++;
        
        if (operationCount >= 500) {
          await batch.commit();
          operationCount = 0;
        }
      }
      
      if (operationCount > 0) {
        await batch.commit();
      }
    }
  }
  
  // Now delete all customers
  await deleteCollectionByTenantId('customers', tenantId);
}

/**
 * Delete all documents in a collection related to a tenant
 */
async function deleteCollectionByTenantId(collectionName: string, tenantId: string): Promise<void> {
  const q = query(
    collection(db, collectionName),
    where('companyId', '==', tenantId)
  );

  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return;
  }

  const batch = writeBatch(db);
  let operationCount = 0;
  
  // Add each document to the deletion batch
  for (const docSnapshot of snapshot.docs) {
    batch.delete(docSnapshot.ref);
    operationCount++;
    
    // If we reach the batch limit, commit and create a new batch
    if (operationCount >= 500) {
      await batch.commit();
      operationCount = 0;
    }
  }
  
  // Commit any remaining operations
  if (operationCount > 0) {
    await batch.commit();
  }
}

/**
 * Delete any company settings or profiles
 */
async function deleteCompanySettings(tenantId: string): Promise<void> {
  // Try to delete from settings collection first
  try {
    const settingsDoc = doc(db, 'settings', tenantId);
    const settingsSnapshot = await getDoc(settingsDoc);
    
    if (settingsSnapshot.exists()) {
      await deleteDoc(settingsDoc);
    }
  } catch (error) {
    console.error('Error deleting company settings:', error);
    // Continue with deletion process even if settings deletion fails
  }
  
  // Delete from companyProfile if it exists
  try {
    const profileQuery = query(
      collection(db, 'companyProfile'),
      where('companyId', '==', tenantId),
      limit(1)
    );
    
    const profileSnapshot = await getDocs(profileQuery);
    
    if (!profileSnapshot.empty) {
      await deleteDoc(profileSnapshot.docs[0].ref);
    }
  } catch (error) {
    console.error('Error deleting company profile:', error);
    // Continue with deletion process even if profile deletion fails
  }
} 